import { Loader2, Plus } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { TemplateCard } from "@/components/composer/templates/TemplateCard";
import { templateKind, type ExcerptusTemplate, type UserTemplate } from "@/lib/composerTemplates";
import type { TemplateSelection } from "@/components/composer/templates/templateTypes";
import type { PreviewState } from "./useTemplatesPanel";

type SortableUserCardProps = {
  template: UserTemplate;
  children: (dragProps: React.HTMLAttributes<HTMLButtonElement>) => React.ReactNode;
};

const SortableUserCard = ({ template, children }: SortableUserCardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: template.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners } as React.HTMLAttributes<HTMLButtonElement>)}
    </div>
  );
};

type RenderOpts = {
  source: "excerptus" | "user";
  badge: string;
  extra?: Partial<React.ComponentProps<typeof TemplateCard>>;
  dragProps?: React.HTMLAttributes<HTMLButtonElement>;
};

type Props = {
  loading: boolean;
  isOwner: boolean;
  filteredExcerptus: ExcerptusTemplate[];
  filteredUser: UserTemplate[];
  updateDisabledReason?: string;
  getCurrentDoc?: () => unknown;
  onSelect: (s: TemplateSelection) => void;
  onPushToCanvas: (t: { name: string; intro_message?: string | null; default_structure: unknown }) => void;
  onPreview: (p: PreviewState) => void;
  onClone: (t: ExcerptusTemplate) => void;
  onDuplicateMine: (t: UserTemplate) => void;
  onEdit: (t: UserTemplate) => void;
  onDelete: (t: UserTemplate) => void;
  onUpdateFromDraft: (t: UserTemplate) => void;
  onCreateNew: () => void;
  onDragEnd: (e: DragEndEvent) => void;
};

export const TemplateLists = ({
  loading, isOwner, filteredExcerptus, filteredUser, updateDisabledReason, getCurrentDoc,
  onSelect, onPushToCanvas, onPreview, onClone, onDuplicateMine, onEdit, onDelete, onUpdateFromDraft,
  onCreateNew, onDragEnd,
}: Props) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const renderCard = (t: ExcerptusTemplate | UserTemplate, opts: RenderOpts) => {
    const k = templateKind(t);
    const useHandler = () =>
      onSelect({
        source: opts.source,
        templateId: t.id,
        name: t.name,
        contentType: t.content_type,
        introMessage: t.intro_message,
        wizardQuestions: t.wizard_questions,
        defaultStructure: t.default_structure,
      });
    return (
      <TemplateCard
        name={t.name}
        description={t.description}
        contentType={t.content_type}
        kind={k}
        badge={opts.badge}
        dragHandleProps={opts.dragProps}
        onPushToCanvas={k === "static" ? () => onPushToCanvas(t) : undefined}
        onUse={k === "dynamic" ? useHandler : undefined}
        {...(opts.extra ?? {})}
      />
    );
  };

  return (
    <>
      <TabsContent value="excerptus" className="flex-1 space-y-3 overflow-auto pr-1">
        {loading ? (
          <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filteredExcerptus.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/60 p-3 text-xs text-muted-foreground">
            No published Excerptus templates match this filter.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredExcerptus.map((t) => (
              <div key={t.id}>
                {renderCard(t, {
                  source: "excerptus",
                  badge: "Excerptus",
                  extra: {
                    onPreview: () => onPreview({ kind: "excerptus", template: t }),
                    onClone: isOwner ? () => onClone(t) : undefined,
                  },
                })}
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="mine" className="flex-1 space-y-3 overflow-auto pr-1">
        <div className="flex items-center justify-end">
          {isOwner && (
            <Button size="sm" onClick={onCreateNew}>
              <Plus className="h-3.5 w-3.5" />
              New template
            </Button>
          )}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filteredUser.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/60 p-3 text-xs text-muted-foreground">
            No templates saved yet. Save a copy of an Excerptus template, or use
            <span className="font-medium"> Save current Draft as template</span> after shaping a draft.
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={filteredUser.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-3">
                {filteredUser.map((t) => (
                  <SortableUserCard key={t.id} template={t}>
                    {(dragProps) =>
                      renderCard(t, {
                        source: "user",
                        badge: "Mine",
                        dragProps: isOwner ? dragProps : undefined,
                        extra: {
                          onPreview: () => onPreview({ kind: "user", template: t }),
                          onEdit: isOwner ? () => onEdit(t) : undefined,
                          onDelete: isOwner ? () => onDelete(t) : undefined,
                          onClone: isOwner ? () => onDuplicateMine(t) : undefined,
                          onUpdateFromDraft:
                            isOwner && getCurrentDoc ? () => onUpdateFromDraft(t) : undefined,
                          updateFromDraftDisabledReason: updateDisabledReason,
                        },
                      })
                    }
                  </SortableUserCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </TabsContent>
    </>
  );
};
